import React, { useEffect, useRef } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../store/appStore";
import { useUserState } from "../../context/user";
// Components
import { LandingPageHeader } from "./components/header/LandingPageHeader.js";
import { MainMenu } from "./components/menu/MainMenu.js";
// Drafts, Submits, Approvals, Reporting, Sampling Events
import { DraftsTab } from "./components/drafts/DraftsTab.js";
import { SubmitsTab } from "./components/submits/SubmitsTab.js";
import { ApprovalsTab } from "./components/approvals/ApprovalsTab.js";
// Reporting
import { DelReportingTab } from "./components/reports/DelReportingTab.js";
import { ReportingPage } from "../reportingPage/ReportingPage.js";
// Sampling Events
import { SamplingEventsTab } from "./components/sampling_events/SamplingEventsTab.js";
// Calibration Logs
import { CalibrationLogsTab } from "./components/calibration_logs/CalibrationLogsTab.js";
import { AdminPage } from "../adminPage/AdminPage.js";
import { SafetyFormsPage } from "../safetyPage/SafetyFormsPage.js";
// Styles
import styles from "./DelLandingPage.module.css";
import { saveData, loadData, makeKey } from "../../services/IndexedDBService";
// RouteUtils
import { canonicalizeHomeLocation } from "../../services/RouteUtils";
import { canonicalizeSamplingEventsLocation } from "../../services/SamplingEventRouteUtils";
import { canonicalizeCalibrationLogBookLocation } from "../../services/CalibrationLogBookRouteUtils";
import { canonicalizeSafetyFormLocation } from "../../services/SafetyFormRouteUtils";

function DelLandingPage() {
  // const { state, dispatch } = useGlobalState();
  const { currentUser, loading } = useUserState();
  const location = useLocation();
  const navigate = useNavigate();
  // App Store stuffz

  const { activeTab } = useAppStore((s) => s.navigation, shallow);
  const { isMenuOpen } = useAppStore((s) => s.mainMenu, shallow);
  const setDeviceFlags = useAppStore(
    (s) => s.landingPageActions?.setDeviceFlags
  );

  // App Store actions
  const initializeAppData = useAppStore(
    (s) => s.delListsActions.fetchInitialDelLists
  );

  const syncTabWithUrl = useAppStore((s) => s.navigationActions.syncTabWithUrl);

  // Run initialize DEL lists once per user
  const initedForUserRef = useRef(null);
  useEffect(() => {
    if (!currentUser) return;
    const key = currentUser.employee_id;
    if (initedForUserRef.current === key) return;
    initedForUserRef.current = key;
    initializeAppData(key);
  }, [currentUser?.employee_id, initializeAppData]);

  // Fetch static lists (projects/employees/units/positions/sites) once per user
  const staticLoadedRef = useRef(null);
  useEffect(() => {
    if (!currentUser) {
      staticLoadedRef.current = null;
      return;
    }
    const key = currentUser.employee_id;
    if (staticLoadedRef.current === key) return;
    staticLoadedRef.current = key;

    const {
      projectsActions,
      employeesActions,
      unitsActions,
      employeePositionsActions,
      samplingSitesActions,
    } = useAppStore.getState();

    projectsActions?.fetchProjects?.();
    projectsActions?.fetchInactiveProjects?.();
    employeesActions?.fetchEmployees?.();
    employeesActions?.fetchTerminatedEmployees?.();
    unitsActions?.fetchUnits?.();
    employeePositionsActions?.fetchEmployeePositions?.();
    samplingSitesActions?.fetchSamplingSites?.();
  }, [currentUser?.employee_id]);

  useEffect(() => {
    syncTabWithUrl(location.pathname);
  }, [location.pathname, syncTabWithUrl]);

  // Detect touch/tablet and track effective viewport (handles iOS keyboard)
  useEffect(() => {
    const getViewportSize = () => {
      const vv = window.visualViewport;
      if (vv && typeof vv.width === "number" && typeof vv.height === "number") {
        return { width: Math.round(vv.width), height: Math.round(vv.height) };
      }
      return { width: window.innerWidth, height: window.innerHeight };
    };

    const detectIsTouch = () => {
      try {
        return (
          "ontouchstart" in window ||
          (navigator &&
            (navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0))
        );
      } catch (_) {
        return false;
      }
    };

    const detectIsTablet = (width, isTouch) => {
      // Support larger tablets (iPad 10/Pro in landscape)
      return !!isTouch && width >= 768 && width <= 1366;
    };

    const updateFlags = () => {
      const { width, height } = getViewportSize();
      const isTouch = detectIsTouch();
      const isTablet = detectIsTablet(width, isTouch);
      setDeviceFlags?.({
        isTouch,
        isTablet,
        viewportWidth: width,
        viewportHeight: height,
      });
    };

    updateFlags();
    window.addEventListener("resize", updateFlags);
    window.addEventListener("orientationchange", updateFlags);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateFlags);
    }
    return () => {
      window.removeEventListener("resize", updateFlags);
      window.removeEventListener("orientationchange", updateFlags);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateFlags);
      }
    };
  }, [setDeviceFlags]);

  // Canonicalize /home URLs (including sampling events and safety forms) once per location change
  useEffect(() => {
    const nextHome = canonicalizeHomeLocation(location);
    const nextSampling = canonicalizeSamplingEventsLocation(location);
    const nextCalibration = canonicalizeCalibrationLogBookLocation(location);
    const nextSafety = canonicalizeSafetyFormLocation(location);
    const next = nextHome || nextSampling || nextCalibration || nextSafety;
    if (next) navigate(next, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Save last visited /home subroute for refresh fallback (include query)
  useEffect(() => {
    if (!currentUser) return;
    if (!location.pathname.startsWith("/home")) return;
    const key = makeKey({
      userId: currentUser.employee_id,
      tab: "lastHomePath",
    });
    saveData(key, `${location.pathname}${location.search || ""}`).catch(() => {});
  }, [currentUser?.employee_id, location.pathname, location.search]);

  // Redirect bare /home to last subroute (or drafts) after login/refresh
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!currentUser) return;
    if (location.pathname === "/home") {
      const key = makeKey({
        userId: currentUser.employee_id,
        tab: "lastHomePath",
      });
      loadData(key)
        .then((res) => {
          const last = res?.data;
          redirectedRef.current = true;
          if (last && last !== "/home") {
            navigate(last, { replace: true });
          } else {
            navigate("/home/drafts", { replace: true });
          }
        })
        .catch(() => {
          redirectedRef.current = true;
          navigate("/home/drafts", { replace: true });
        });
    }
  }, [currentUser?.employee_id, location.pathname, navigate]);

  // Add loading state to prevent premature rendering
  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingMessage}>Loading application data...</div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <LandingPageHeader />
      <MainMenu />
      <main
        className={`${styles.mainContent} ${isMenuOpen ? styles.shifted : ""}`}
      >
        <Routes>
          {/* Explicit tab routes — URL is the source of truth */}
          <Route path="drafts" element={<DraftsTab />} />
          <Route path="submitted" element={<SubmitsTab />} />
          {/* Alias to support users typing /home/submits */}
          <Route path="submits" element={<SubmitsTab />} />
          <Route path="approvals" element={<ApprovalsTab />} />
          <Route path="del_reporting" element={<DelReportingTab />} />
          <Route path="reporting" element={<ReportingPage />} />
          <Route
            path="admin"
            element={
              currentUser.admin === 1 ? (
                <AdminPage />
              ) : (
                <Navigate to="/home/drafts" replace />
              )
            }
          />
          {/* Safety Forms Routes */}
          <Route path="near_miss" element={<SafetyFormsPage />} />
          <Route
            path="near_miss/near_miss_id=:nearMissId"
            element={<SafetyFormsPage />}
          />
          <Route
            path="near_miss/:nearMissParam"
            element={<SafetyFormsPage />}
          />

          <Route path="safety_audit" element={<SafetyFormsPage />} />
          <Route
            path="safety_audit/safety_audit_id=:safetyAuditId"
            element={<SafetyFormsPage />}
          />
          <Route
            path="safety_audit/:safetyAuditParam"
            element={<SafetyFormsPage />}
          />

          <Route path="incident_investigation" element={<SafetyFormsPage />} />
          <Route
            path="incident_investigation/incident_investigation_id=:incidentInvestigationId"
            element={<SafetyFormsPage />}
          />
          <Route
            path="incident_investigation/:incidentInvestigationParam"
            element={<SafetyFormsPage />}
          />

          {/* SDS Lookup - every logged-in user */}
          <Route path="sds_lookup" element={<SafetyFormsPage />} />

          {/* Safety Form Reporting - Access controlled route */}
          <Route
            path="safety_form_reporting"
            element={
              currentUser &&
              (currentUser.safety_team_member === 1 ||
                currentUser.access_level >= 2) ? (
                <SafetyFormsPage />
              ) : (
                <Navigate to="/home/drafts" replace />
              )
            }
          />
          <Route
            path="safety_form_reporting/near_miss_id=:nearMissId"
            element={
              currentUser &&
              (currentUser.safety_team_member === 1 ||
                currentUser.access_level >= 2) ? (
                <SafetyFormsPage />
              ) : (
                <Navigate to="/home/drafts" replace />
              )
            }
          />
          <Route
            path="safety_form_reporting/safety_audit_id=:safetyAuditId"
            element={
              currentUser &&
              (currentUser.safety_team_member === 1 ||
                currentUser.access_level >= 2) ? (
                <SafetyFormsPage />
              ) : (
                <Navigate to="/home/drafts" replace />
              )
            }
          />
          <Route
            path="safety_form_reporting/:formParam"
            element={
              currentUser &&
              (currentUser.safety_team_member === 1 ||
                currentUser.access_level >= 2) ? (
                <SafetyFormsPage />
              ) : (
                <Navigate to="/home/drafts" replace />
              )
            }
          />

          {/* Deep links with canonical del_id pattern */}
          <Route path="drafts/del_id=:delId" element={<DraftsTab />} />
          {/* Fallback matcher to accept any suffix and parse inside component (handles edge cases) */}
          <Route path="drafts/:delParam" element={<DraftsTab />} />
          <Route path="submitted/del_id=:delId" element={<SubmitsTab />} />
          <Route path="submits/del_id=:delId" element={<SubmitsTab />} />
          {/* Fallback matchers for submitted to accept any suffix and parse in component */}
          <Route path="submitted/:delParam" element={<SubmitsTab />} />
          <Route path="submits/:delParam" element={<SubmitsTab />} />
          <Route path="approvals/del_id=:delId" element={<ApprovalsTab />} />
          {/* Fallback matcher to accept any suffix and parse inside component (handles edge cases) */}
          <Route path="approvals/:delParam" element={<ApprovalsTab />} />

          {/* Sampling / Admin */}
          <Route path="sampling_events/*" element={<SamplingEventsTab />} />
          {/* Calibration Logs (standalone) */}
          <Route path="calibration_logs/*" element={<CalibrationLogsTab />} />

          {/* Default within /home goes to drafts (lastHomePath effect may override) */}
          <Route index element={<Navigate to="drafts" replace />} />
          {/* Fallback */}
          <Route path="*" element={<Navigate to="drafts" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default DelLandingPage;
